import { useId, useState } from 'react';
import { Radio } from 'antd';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { useDatUuTienToken } from '../hooks/useHanMucTokenAi';
import { canXacNhanDoiUuTien, congNgay, tokenSeMatCuoiThang } from '../nghiep-vu/cap-them-token';
import type { AiTokenPriority, AiTokenQuota } from '../types';
import { CanhBaoTrongHop, LoiTrongHop } from './phan-form';

const NHAN: Record<AiTokenPriority, KhoaDich> = {
  HET_HAN_TRUOC: 'tokenAi.uuTien.HET_HAN_TRUOC',
  MUA_THEM_TRUOC: 'tokenAi.uuTien.MUA_THEM_TRUOC',
};
const CAC_UU_TIEN: AiTokenPriority[] = ['HET_HAN_TRUOC', 'MUA_THEM_TRUOC'];

/**
 * "Thứ tự dùng token" (tham số `ai_quota.uu_tien`, ADR-0091 mục 4): `PUT …/ai-token-quota/priority`. Platform Admin
 * (`TENANT.UPDATE`) và Tenant Admin của chính tổ chức sửa được; không được sửa thì chỉ hiện lựa chọn đang áp.
 *
 * `MUA_THEM_TRUOC` làm mất token gói tài nguyên không dùng hết trong tháng: chọn nó phải qua hộp xác nhận, và khi đang
 * áp thì cảnh báo luôn hiện, kèm số token đang có nguy cơ mất. Đổi về mặc định thì làm ngay.
 */
export function ThuTuDungToken({ q, tenantId, duocSua }: { q: AiTokenQuota; tenantId: string; duocSua: boolean }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const id = useId();
  const dat = useDatUuTienToken(tenantId);
  const [canXacNhan, setCanXacNhan] = useState(false);
  const seMat = tokenSeMatCuoiThang(q.priority, q);

  const chon = (moi: AiTokenPriority) => {
    dat.reset();
    if (canXacNhanDoiUuTien(q.priority, moi)) setCanXacNhan(true);
    else if (moi !== q.priority) dat.mutate(moi);
  };

  return (
    <section aria-labelledby={`${id}-tieu-de`} className="space-y-2">
      <h3 id={`${id}-tieu-de`} className="text-sm font-semibold text-content-primary">
        {t('tokenAi.uuTienTieuDe')}
      </h3>
      {duocSua ? (
        <Radio.Group
          aria-labelledby={`${id}-tieu-de`}
          className="flex flex-col gap-2"
          value={q.priority}
          disabled={dat.isPending}
          onChange={(e) => chon(e.target.value as AiTokenPriority)}
        >
          {CAC_UU_TIEN.map((p) => (
            <Radio key={p} value={p}>
              <span className="text-sm text-content-primary">{t(NHAN[p])}</span>
            </Radio>
          ))}
        </Radio.Group>
      ) : (
        <p className="text-sm text-content-secondary">
          <span className="text-content-primary">{t(NHAN[q.priority])}</span>
        </p>
      )}
      {q.priority === 'MUA_THEM_TRUOC' && q.base_limit !== null && (
        <CanhBaoTrongHop>
          <p>{t('tokenAi.uuTienCanhBao')}</p>
          {seMat > 0 && (
            <p className="tabular-nums">
              {t('tokenAi.uuTienCanhBaoSo', { so: soVN(seMat, locale), ngay: ngayVN(congNgay(q.resets_at.slice(0, 10), -1)) })}
            </p>
          )}
        </CanhBaoTrongHop>
      )}
      {Boolean(dat.error) && <LoiTrongHop>{t('tokenAi.loiUuTien', { loi: dichLoi(dat.error) })}</LoiTrongHop>}

      <HopXacNhanThaoTac
        open={canXacNhan}
        tieuDe={t('tokenAi.xacNhanUuTien')}
        than={t('tokenAi.xacNhanUuTienMo')}
        nhanXacNhan={t('tokenAi.nutUuTienMua')}
        dangChay={dat.isPending}
        loi={dat.error ? dichLoi(dat.error) : null}
        onClose={() => setCanXacNhan(false)}
        onXacNhan={() => dat.mutate('MUA_THEM_TRUOC', { onSuccess: () => setCanXacNhan(false) })}
      />
    </section>
  );
}
